/**
 * When the System run panel may apply (MOT-780). Pure, so the API tests can
 * pin it without rendering React.
 *
 * Apply is allowed only after a dry run was reviewed, that dry run would
 * change at least one record, the receipt is still the one for the options
 * on screen, and (for a paged lens walk) the walk reached the end.
 */

export type RunPhase = 'idle' | 'running' | 'reviewed';

export interface RunGateState {
  phase: RunPhase;
  changeCount: number;
  dryRunId: string | null;
  /** Options serialized when the receipt was issued. */
  receiptOptions: string | null;
  /** Options on screen now. */
  options: string;
  /** False while a lens walk stopped before the last page. Other scripts set true once reviewed. */
  walkComplete: boolean;
}

export function optionsMatchReceipt(state: Pick<RunGateState, 'options' | 'receiptOptions'>): boolean {
  return state.receiptOptions !== null && state.options === state.receiptOptions;
}

export function canApply(state: RunGateState): boolean {
  return (
    state.phase === 'reviewed' &&
    state.changeCount > 0 &&
    Boolean(state.dryRunId) &&
    optionsMatchReceipt(state) &&
    state.walkComplete
  );
}

/**
 * Drop the review. A request that is already running stays running: dropping
 * back to idle would re-enable Dry Run, and a second preview could unlock
 * Apply while the first apply is still writing.
 */
export function clearReview(state: RunGateState, options: string): RunGateState {
  return {
    phase: state.phase === 'running' ? 'running' : 'idle',
    changeCount: 0,
    dryRunId: null,
    receiptOptions: null,
    options,
    walkComplete: false,
  };
}

/** Editing an option throws away the review. Apply stays locked until a new dry run. */
export function afterOptionChange(state: RunGateState, options: string): RunGateState {
  if (options === state.options) return state;
  return clearReview(state, options);
}

/**
 * A completion may settle the panel only while it still owns the running
 * request. A newer run, or a panel that has already moved on, is left alone.
 */
export function classifyCompletion(
  startedGeneration: number,
  generation: number,
  activeGeneration: number | null,
): 'ignore' | 'superseded' | 'current' {
  if (activeGeneration === null || startedGeneration !== activeGeneration) return 'ignore';
  return startedGeneration === generation ? 'current' : 'superseded';
}

export interface DryRunSettlement {
  changeCount: number;
  dryRunId: string | null;
  walkComplete: boolean;
  /** Options the request actually used. */
  options: string;
}

/**
 * Publish a dry run only if nothing has superseded it. An option change bumps
 * the generation, so a late result cannot become the review for the new options.
 */
export function finishDryRun(
  startedGeneration: number,
  generation: number,
  result: DryRunSettlement,
): Pick<RunGateState, 'phase' | 'changeCount' | 'dryRunId' | 'receiptOptions' | 'walkComplete'> {
  if (startedGeneration !== generation) {
    return { phase: 'idle', changeCount: 0, dryRunId: null, receiptOptions: null, walkComplete: false };
  }
  return {
    phase: 'reviewed',
    changeCount: result.changeCount,
    dryRunId: result.dryRunId,
    receiptOptions: result.options,
    walkComplete: result.walkComplete,
  };
}

/** A finished apply. A superseded run settles to idle and does not keep a receipt. */
export function finishApply(
  startedGeneration: number,
  generation: number,
  ok: boolean,
): { phase: RunPhase; clearReceipt: boolean } {
  if (startedGeneration !== generation) return { phase: 'idle', clearReceipt: true };
  if (!ok) return { phase: 'reviewed', clearReceipt: false };
  return { phase: 'idle', clearReceipt: true };
}
